// KindPicker — "what sort of thing is this", as one control.
//
// ── WHY IT IS A COMPONENT AND NOT A STEP OF THE WIZARD ──────────────────────
// It started as step one of `NewItemModal`, where the answer decides what the
// rest of the form even asks. That question is not the wizard's: the scan and
// quote lanes ask it per line, on a card, thirty times before one Confirm. Two
// copies of a picker over one table is how two screens learn to disagree about
// what a kind is, so the picker moved out and the wizard became a caller.
//
// ── WHAT THE ROW HAS TO SAY ─────────────────────────────────────────────────
// A kind carries two facts a person needs at once: what it is called, and what
// a code of that sort LOOKS like. `exampleCode` is the second one, and it is
// the entire explanation of "board code versus short code" in one glance where
// a sentence would not land. The chip repeats it in words for anybody scanning
// the right edge.
//
// Boards are grouped first because EntityCombobox draws a divider above the
// first of a RUN: an interleaved list would draw one per row.
//
// ── NOBODY LEAVES ───────────────────────────────────────────────────────────
// Per the standing rule in web/CLAUDE.md, a picker whose value does not exist
// yet offers to create it in place. The pinned last row opens `ItemKindForm`
// over whatever surface this is sitting on, and the created kind lands straight
// back in the field that asked for it. `allowCreate={false}` is for a surface
// where minting a taxonomy row mid-task would be the wrong offer.
//
// ── THE LIST NOT ARRIVING IS A STATE, NOT A BLANK ───────────────────────────
// `useItemKindsStore` swallows a failed fetch and records it, because a picker
// that cannot load its options is not a page-level failure. So this says so and
// offers the retry itself: a caller that had to render its own banner would be
// a caller that could forget to.

import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '../components/ui/Button';
import { Alert } from '../components/ui/Alert';
import { useApi } from '../apiHelpers';
import EntityCombobox, { type ComboboxOption } from '../components/EntityCombobox';
import ItemKindForm from '../Admin/ItemKindForm';
import { laneInput, laneLabel } from '../components/review/fieldStyles';
import useItemKindsStore, { type ItemKind } from '../stores/useItemKindsStore';
import { exampleCode } from './skuGrammar';

/** The sentinel on the pinned last row. No kind key contains an underscore. */
const NEW_KIND = '__new_kind__';

export interface KindPickerProps {
  /** The chosen kind's key, or null for "nobody has said yet". */
  value: string | null;
  /** The whole row, not the key: every caller needs `code_mode` immediately. */
  onChange: (kind: ItemKind) => void;
  /** Offer the pinned "New kind…" row. Default true. */
  allowCreate?: boolean;
  autoFocus?: boolean;
  id?: string;
  label?: string;
  placeholder?: string;
  /** `sm` for a picker inside a card or a table row. Default `md`. */
  size?: 'sm' | 'md';
  className?: string;
  inputClassName?: string;
  labelClassName?: string;
  /**
   * The part number this surface is looking at, handed to `ItemKindForm` so it
   * can catch a kind being named after it. Optional: a caller with no part
   * number in view has nothing to warn about.
   */
  partNumberHint?: string | null;
}

// IS the lane input, not a copy of it. A picker on a card has to be the same
// height as the boxes beside it or the row reads as broken, and the two strings
// had already drifted apart once.

const KindPicker: React.FC<KindPickerProps> = ({
  value,
  onChange,
  allowCreate = true,
  autoFocus = false,
  id,
  label,
  placeholder,
  size = 'md',
  className,
  inputClassName,
  labelClassName,
  partNumberHint = null,
}) => {
  const api = useApi();
  // Subscribe to the raw array and derive. The store's own helpers return a
  // fresh array per call, so calling one inside a selector re-renders forever.
  const kinds = useItemKindsStore((s) => s.kinds);
  const kindsLoading = useItemKindsStore((s) => s.loading);
  const kindsError = useItemKindsStore((s) => s.error);
  const loadKinds = useItemKindsStore((s) => s.load);
  const [addingKind, setAddingKind] = useState(false);

  // Cached for the session and deduplicated in the store, so thirty of these on
  // one scan screen are still one GET.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadKinds(api); }, []);

  const options = useMemo<ComboboxOption[]>(() => {
    const ordered = kinds
      .filter((k) => k.is_active)
      .slice()
      .sort((a, b) => {
        const aBoard = a.code_mode === 'structured';
        const bBoard = b.code_mode === 'structured';
        if (aBoard !== bBoard) return aBoard ? -1 : 1;
        return a.sort_order - b.sort_order || a.key.localeCompare(b.key);
      });
    const rows: ComboboxOption[] = ordered.map((k) => ({
      id: k.key,
      label: k.label || k.key,
      sublabel: exampleCode(k.key, k.code_mode),
      chip: k.code_mode === 'structured' ? 'Board' : 'Short code',
      group: k.code_mode === 'structured' ? 'Boards' : 'Everything else',
      data: k,
    }));
    if (allowCreate) {
      rows.push({
        id: NEW_KIND,
        label: 'New kind…',
        sublabel: 'For a sort of thing the catalog has never held',
        pinned: true,
      });
    }
    return rows;
  }, [kinds, allowCreate]);

  return (
    <div className={className}>
      <EntityCombobox
        id={id}
        label={label}
        autoFocus={autoFocus}
        options={options}
        value={value}
        placeholder={
          kindsLoading && !kinds.length
            ? 'Loading kinds…'
            : placeholder || 'Board, grommet, cable, anything'
        }
        inputClassName={inputClassName || (size === 'sm' ? laneInput : undefined)}
        labelClassName={labelClassName || (size === 'sm' ? laneLabel : undefined)}
        // No emptyMessage: the pinned row survives any query, so the list is
        // never empty and a query that matches nothing lands on exactly the row
        // that answers it. Without the create row there is nothing to land on,
        // so that case gets a sentence.
        emptyMessage={allowCreate ? undefined : 'No kind by that name.'}
        onSelect={(option) => {
          if (!option) return;
          if (option.id === NEW_KIND) { setAddingKind(true); return; }
          const kind = option.data as ItemKind | undefined;
          if (kind) onChange(kind);
        }}
      />

      <Alert
        show={!!kindsError}
        tone="warning"
        live="alert"
        className="mt-1.5"
        action={
          <Button variant="quiet" size="sm" onClick={() => loadKinds(api, true)} disabled={kindsLoading} className="shrink-0">
            {kindsLoading ? 'Trying…' : 'Try again'}
          </Button>
        }
      >
        {kindsError}
    </Alert>

      {addingKind && (
        // Submit events bubble through the Dialog portal to the React parent,
        // so without this the host's own form (Change kind, New item) submits too.
        <div onSubmit={(e) => e.stopPropagation()}>
          <ItemKindForm
            partNumberHint={partNumberHint}
            onClose={() => setAddingKind(false)}
            onCreated={(created: ItemKind) => {
              // Straight into the answer slot. The store already holds it, so the
              // caller's `value` resolves on this render rather than after a
              // refetch.
              setAddingKind(false);
              onChange(created);
            }}
          />
        </div>
      )}
    </div>
  );
};

export default KindPicker;
